import { styled, useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Body2, HeadingMenuSelected } from '@tigergraph/app-ui-lib/typography';
import Starter from '@/assets/starter.png';
import Professional from '@/assets/professional.png';
import {
  CreateSolution,
  ExploreGraph,
  GraphStudio,
  GSQLEditorXS,
  LoadData,
  Monitor,
} from '@/components/tutorial/icons.tsx';
import { GraphIcon } from '@/pages/home/icons.tsx';
import { useNavigate } from 'react-router';
import { useTutorialContext } from '@/components/tutorial/tutorialContext.tsx';
import { useConfig } from '@/lib/config.tsx';
import { useOrgContext } from '@/contexts/orgContext.tsx';
import { getToolsLink } from '@/hooks/useToolsLink.ts';
import { useWorkspaceContext } from '@/contexts/workspaceContext.tsx';

export function GettingStartTutorial({ onClose }: { onClose: () => void }) {
  const [css, theme] = useStyletron();
  const navigate = useNavigate();
  const { workspace } = useTutorialContext();
  const { currentWorkspace } = useWorkspaceContext();
  const { appConfig } = useConfig();
  const { currentOrg } = useOrgContext();

  const Section = styled('div', {
    padding: '8px',
    border: `1px solid ${theme.colors.gray200}`,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: theme.colors.gray50,
    },
    ':active': {
      backgroundColor: theme.colors.gray100,
    },
  });

  const SectionHeader = styled('div', {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '8px',
  });

  const SectionLabel = styled('div', {
    fontSize: '12px',
    lineHeight: '16px',
    color: theme.colors.black03,
    marginLeft: '8px',
    borderLeft: `1px dashed ${theme.colors.gray200}`,
    paddingLeft: '16px',
  });

  const ParagraphLabel = styled('div', ({ $theme }) => {
    return {
      fontSize: '12px',
      lineHeight: '16px',
      color: theme.colors.black03,
    };
  });

  const Divider = () => {
    return (
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          color: theme.colors.gray400,
          gap: '3px',
        })}
      >
        <div
          className={css({
            width: '1px',
            height: '30%',
            backgroundColor: theme.colors.gray100,
          })}
        ></div>
        or
        <div
          className={css({
            width: '1px',
            height: '30%',
            backgroundColor: theme.colors.gray100,
          })}
        ></div>
      </div>
    );
  };

  const navigateToStudio = () => {
    if (!workspace && !currentWorkspace) {
      return;
    }
    let ws = workspace ?? currentWorkspace;
    const url = getToolsLink(appConfig, currentOrg, ws, '/studio');
    window.open(url, '_blank');
  };

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <div>
      <ParagraphLabel>
        Once you&apos;ve set up your workspace, you can easily define a schema, load your data, and start writing
        queries using the user-friendly GSQL query language. This guide will walk you through each step, whether
        you&apos;re new to graphs or an experienced professional, making it easy to get started and explore the power of
        TigerGraph Cloud.
      </ParagraphLabel>
      <div
        className={css({
          display: 'flex',
          gap: '24px',
        })}
      >
        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            width: '427px',
            height: '467px',
          })}
        >
          <div
            className={css({
              paddingTop: '16px',
              paddingBottom: '16px',
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
            })}
          >
            <img
              src={Starter}
              style={{
                width: '13px',
              }}
            />
            <HeadingMenuSelected>Starter</HeadingMenuSelected>
          </div>

          <div
            className={css({
              display: 'flex',
              gap: '16px',
              flexDirection: 'column',
            })}
          >
            <Section
              $style={{
                display: 'flex',
                gap: '16px',
                ':hover': {
                  backgroundColor: 'none',
                },
                ':active': {
                  backgroundColor: 'none',
                },
              }}
            >
              <div
                onClick={() => handleNavigate('/ingestion')}
                className={css({
                  ':hover': {
                    backgroundColor: theme.colors.gray50,
                  },
                  ':active': {
                    backgroundColor: theme.colors.gray100,
                  },
                })}
              >
                <SectionHeader>
                  <LoadData />
                  <Body2>Load Data</Body2>
                </SectionHeader>
                <SectionLabel>Load your data into TigerGraph in few clicks.</SectionLabel>
              </div>

              <Divider />

              <div
                onClick={() => handleNavigate('/marketplace/solutions')}
                className={css({
                  ':hover': {
                    backgroundColor: theme.colors.gray50,
                  },
                  ':active': {
                    backgroundColor: theme.colors.gray100,
                  },
                })}
              >
                <SectionHeader>
                  <CreateSolution />
                  <Body2>Create solution</Body2>
                </SectionHeader>
                <SectionLabel>Start with industry standard solution kit.</SectionLabel>
              </div>
            </Section>

            <Section onClick={() => handleNavigate('/editor')}>
              <div>
                <SectionHeader>
                  <GSQLEditorXS />
                  <Body2>GSQL Editor</Body2>
                </SectionHeader>
                <SectionLabel>Edit and run GSQL query on your workspace.</SectionLabel>
              </div>
            </Section>

            <Section onClick={() => handleNavigate('/explore')}>
              <div>
                <SectionHeader>
                  <ExploreGraph />
                  <Body2>Explore Graph</Body2>
                </SectionHeader>
                <SectionLabel>Explore your graph data by pattern search or TigerGraph CoPilot.</SectionLabel>
              </div>
            </Section>
          </div>
        </div>

        <div
          className={css({
            display: 'flex',
            flexDirection: 'column',
            width: '427px',
            height: '467px',
            flexGrow: 1,
          })}
        >
          <div
            className={css({
              paddingTop: '16px',
              paddingBottom: '16px',
              display: 'flex',
              justifyContent: 'center',
              gap: '16px',
            })}
          >
            <img
              src={Professional}
              style={{
                width: '13px',
              }}
            />
            <HeadingMenuSelected>Professional</HeadingMenuSelected>
          </div>

          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            })}
          >
            <div
              className={css({
                display: 'flex',
                gap: '8px',
              })}
            >
              <div
                className={css({
                  display: 'flex',
                  gap: '8px',
                  flexDirection: 'column',
                })}
              >
                <Section
                  $style={{
                    display: 'flex',
                    gap: '16px',
                    borderRight: `1px solid ${theme.colors.gray200}`,
                    ':hover': {
                      backgroundColor: 'none',
                    },
                    ':active': {
                      backgroundColor: 'none',
                    },
                  }}
                >
                  <div
                    className={css({
                      display: 'flex',
                      gap: '8px',
                      flexDirection: 'column',
                    })}
                  >
                    <div
                      onClick={() => handleNavigate(`/editor?showSchema=true&workspace_id=${workspace?.workspace_id}`)}
                      className={css({
                        ':hover': {
                          backgroundColor: theme.colors.gray50,
                        },
                        ':active': {
                          backgroundColor: theme.colors.gray100,
                        },
                      })}
                    >
                      <SectionHeader>
                        <GraphIcon />
                        <Body2>Design Schema</Body2>
                      </SectionHeader>
                      <SectionLabel>Create your first graph model. </SectionLabel>
                    </div>
                    <div
                      onClick={() => handleNavigate('/ingestion')}
                      className={css({
                        ':hover': {
                          backgroundColor: theme.colors.gray50,
                        },
                        ':active': {
                          backgroundColor: theme.colors.gray100,
                        },
                      })}
                    >
                      <SectionHeader>
                        <LoadData />
                        <Body2>Load Data</Body2>
                      </SectionHeader>
                      <SectionLabel>Load your data into TigerGraph in few clicks.</SectionLabel>
                    </div>
                  </div>
                  <Divider />
                  <div
                    onClick={() => handleNavigate('/marketplace/solutions')}
                    className={css({
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      ':hover': {
                        backgroundColor: theme.colors.gray50,
                      },
                      ':active': {
                        backgroundColor: theme.colors.gray100,
                      },
                    })}
                  >
                    <SectionHeader>
                      <CreateSolution />
                      <Body2>Create solution</Body2>
                    </SectionHeader>
                    <SectionLabel>Start with industry standard solution kit.</SectionLabel>
                  </div>
                </Section>
                <Section
                  onClick={() => handleNavigate('/editor')}
                  $style={{
                    borderRight: `1px solid ${theme.colors.gray200}`,
                  }}
                >
                  <SectionHeader>
                    <GSQLEditorXS />
                    <Body2>GSQL Editor</Body2>
                  </SectionHeader>
                  <SectionLabel>Edit and run GSQL query on your workspace.</SectionLabel>
                </Section>
              </div>
              <Section
                onClick={() => navigateToStudio()}
                $style={{
                  borderLeft: `1px solid ${theme.colors.gray200}`,
                  minWidth: '108px',
                  width: '108px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <div
                  className={css({
                    display: 'flex',
                    justifyContent: 'center',
                  })}
                >
                  <GraphStudio />
                </div>
                <ParagraphLabel $style={{ textAlign: 'center' }}>
                  TigerGraph classic visual development environment.
                </ParagraphLabel>
              </Section>
            </div>

            <Section onClick={() => handleNavigate(`/groups/${workspace?.workgroup_id}?tab=monitor`)}>
              <SectionHeader>
                <Monitor />
                <Body2>Monitor</Body2>
              </SectionHeader>
              <SectionLabel>Monitor the status and performance of your workspace.</SectionLabel>
            </Section>

            <Section onClick={() => handleNavigate('/explore')}>
              <SectionHeader>
                <ExploreGraph />
                <Body2>Explore Graph</Body2>
              </SectionHeader>
              <SectionLabel>Explore your graph data by pattern search or TigerGraph CoPilot.</SectionLabel>
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
}
